import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Flame, ShieldCheck, Moon, Users, MessageSquare, Smartphone, Activity, ArrowRight, Check, CheckCheck, Mic, Play, X } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const FEATURES = [
  { icon: Activity, title: "🚀 Aquecimento Inteligente Baseado no Ritmo do Chip", desc: "Seus chips conversam sozinhos na nuvem com o seu computador inteiramente desligado. Não perca nenhuma comissão ou venda por queda de conexão." },
  { icon: ShieldCheck, title: "🛡️ Blindagem Contra Bloqueios", desc: "Algoritmo inteligente que faz rodízio automático de conversas diárias entre chips reais. Seu número seguro, ativo e totalmente imune ao efeito dominó." },
  { icon: Moon, title: "💤 Ciclo de Descanso Inteligente", desc: "Simulação de rotina diária e pausas estratégicas de descanso. Segurança máxima baseada em comportamento real para manter a atividade do chip em total conformidade com a Meta." },
  { icon: MessageSquare, title: "🎙️ Simulação Neural de Áudios e Interações Coletivas", desc: "Textos, imagens, vídeos, áudios e figurinhas trocados com variações inteligentes do banco global, com status de 'digitando' e 'gravando áudio'." },
  { icon: Users, title: "Grupos controlados", desc: "Chips novos entram automaticamente em grupos do WhatsApp e interagem entre si com segurança." },
  { icon: Smartphone, title: "Chamadas simuladas", desc: "Ligações de voz e vídeo de 15 a 45 segundos entre chips pareados geram histórico orgânico." },
];

const PLAN_ORDER = ["m1", "m5", "m10", "m20", "m50"];

const FAQ_ITEMS = [
  {
    q: "O MaturaMax garante 100% que não serei banido?",
    a: "Não, o bloqueio é uma decisão da Meta, mas o MaturaMax reduz drasticamente os riscos simulando perfeitamente o comportamento humano.",
  },
  {
    q: "Preciso deixar o computador ligado?",
    a: "Não! Nosso sistema roda 100% na nuvem, 24 horas por dia. Você pode desligar tudo.",
  },
  {
    q: "Como funciona o cancelamento?",
    a: "Sem contratos ou fidelidade. Você pode cancelar ou mudar de plano quando quiser direto pelo painel.",
  },
  {
    q: "Quanto tempo leva para aquecer um chip?",
    a: "O ciclo ideal de maturação estrutural leva em média 2 semanas. Nossa inteligência simula uma esteira progressiva com gradação de carga de dados e distribuição randômica de delays. Esse processo eleva organicamente o score de confiança do número nos servidores da Meta até atingir níveis de segurança máxima para operações de tráfego de alta escala.",
  },
  {
    q: "Meus dados estão seguros?",
    a: "Totalmente. Operamos sob uma infraestrutura de microsserviços isolados com criptografia de ponta a ponta (padrão AES-256) e tokens de sessão autenticados. Suas instâncias, históricos de conversas e dados privados rodam em um ambiente de nuvem proprietário e estritamente confidencial, livre de compartilhamento com terceiros ou rastreamento cruzado.",
  },
  {
    q: "Posso usar para enviar spam?",
    a: "Não. O MaturaMax foi desenvolvido estritamente como um software de engenharia reversa para blindagem, contingência e simulação neural de comportamento humano legítimo. A plataforma atua no aquecimento preventivo do chip. O disparo de mensagens em massa sem autorização viola as diretrizes e os termos de serviço da Meta e não é suportado pelo nosso motor de loops.",
  },
];

export default function Landing() {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/payments/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#090D16] text-slate-100" data-testid="landing-page">
      <header className="fixed top-0 inset-x-0 z-40 bg-[#0B111E]/85 backdrop-blur-md border-b border-slate-800/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
              <Flame className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <span className="font-heading font-extrabold text-lg tracking-tight">MaturaMax</span>
              <span className="hidden sm:block text-[10px] text-slate-500 -mt-0.5">maturamax.com.br</span>
            </div>
          </div>
          <div className="flex items-center gap-2.5">
            <Link to="/login">
              <Button variant="ghost" data-testid="landing-nav-login-button"
                className="text-slate-300 hover:text-emerald-300 hover:bg-emerald-500/10 font-medium">
                Entrar
              </Button>
            </Link>
            <Link to="/cadastro">
              <Button data-testid="landing-nav-signup-button"
                className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold shadow-[0_0_20px_rgba(16,185,129,0.25)] transition-all duration-200 hover:-translate-y-0.5">
                Cadastre-se
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <section className="relative pt-32 sm:pt-40 pb-20 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <span className="inline-block text-xs font-semibold px-3.5 py-1.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 uppercase tracking-wider mb-6 animate-fade-up">
            Maturação de Chips
          </span>
          <h1 className="font-heading text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-balance animate-fade-up" style={{ animationDelay: "80ms" }}>
            🛡️ A única Esteira de Maturação com <span className="text-emerald-400">Blindagem Anti-Banimento</span> e Efeito Dominó
          </h1>
          <p className="text-base md:text-lg text-slate-400 mt-6 max-w-2xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: "160ms" }}>
            Deixe seus números aquecendo no piloto automático direto na nuvem. Com nosso sistema exclusivo de Rodízio Seguro Diário, seus chips ganham pontuação máxima de confiança na Meta sem riscos de quedas em cadeia.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-9 animate-fade-up" style={{ animationDelay: "240ms" }}>
            <Link to="/cadastro" className="w-full sm:w-auto">
              <Button data-testid="landing-hero-signup-button"
                className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-bold h-12 px-8 text-base shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-200 hover:-translate-y-0.5">
                Começar agora <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" className="w-full sm:w-auto">
              <Button variant="outline" data-testid="landing-hero-login-button"
                className="w-full sm:w-auto border-slate-700 text-slate-200 hover:bg-slate-800 hover:border-slate-600 h-12 px-8 text-base">
                Já tenho conta
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section id="recursos" className="px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {FEATURES.map((f, i) => (
            <div key={f.title}
              className="bg-[#161F32] border border-slate-800 rounded-xl p-6 transition-all duration-200 hover:-translate-y-1 hover:border-emerald-500/30 hover:shadow-[0_0_25px_rgba(16,185,129,0.1)] animate-fade-up"
              style={{ animationDelay: `${i * 70}ms` }}>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-heading font-bold text-slate-100 mb-1.5">{f.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 sm:px-6 pb-24" data-testid="landing-demo-section">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="animate-fade-up">
            <span className="inline-block text-xs font-semibold px-3.5 py-1.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider mb-5">
              Demonstração ao vivo
            </span>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Veja o MaturaMax em ação</h2>
            <p className="text-sm sm:text-base text-slate-400 leading-relaxed mb-6">
              Assim que você conecta seus chips, o robô assume as conversas na nuvem com comportamento indistinguível de uma pessoa real. É assim que seus números ganham histórico orgânico todos os dias.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-sm text-slate-300 bg-[#161F32] border border-slate-800 rounded-lg px-4 py-3">
                <span className="text-lg">💬</span> Mensagens dinâmicas geradas do banco global
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-300 bg-[#161F32] border border-slate-800 rounded-lg px-4 py-3">
                <span className="text-lg">🎙️</span> Status "Gravando Áudio..." acionado antes dos envios de voz
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-300 bg-[#161F32] border border-slate-800 rounded-lg px-4 py-3">
                <span className="text-lg">💤</span> Entrando em pausa de respiro para simular descanso humano
              </li>
            </ul>
          </div>

          <div className="relative mx-auto w-full max-w-[320px] animate-fade-up" style={{ animationDelay: "150ms" }}>
            <div className="absolute -inset-4 bg-gradient-to-br from-emerald-500/25 via-cyan-500/15 to-transparent rounded-[3rem] blur-2xl" />
            <div className="relative bg-[#0B141A] border border-slate-700 rounded-[2.5rem] p-2.5 shadow-2xl">
              <div className="bg-[#111B21] rounded-[2rem] overflow-hidden">
                <div className="flex items-center gap-2.5 px-4 py-3 bg-[#1F2C34] border-b border-slate-800/50">
                  <div className="w-8 h-8 rounded-full bg-emerald-500/25 border border-emerald-500/40 flex items-center justify-center">
                    <Smartphone className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-100 truncate">Chip Vendas 01</p>
                    <p className="text-[10px] text-emerald-400">online</p>
                  </div>
                  <div className="flex gap-3 text-slate-500">
                    <Mic className="w-3.5 h-3.5" />
                    <Play className="w-3.5 h-3.5" />
                  </div>
                </div>
                <div className="bg-[#0B141A] p-3.5 space-y-2.5 min-h-[300px]">
                  <div className="bg-[#1F2C34] rounded-lg rounded-tl-none px-3 py-2 max-w-[80%]">
                    <p className="text-xs text-slate-200">Bom dia! Tudo bem por aí?</p>
                    <p className="text-[9px] text-slate-500 text-right mt-0.5">08:12</p>
                  </div>
                  <div className="bg-[#005C4B] rounded-lg rounded-tr-none px-3 py-2 max-w-[80%] ml-auto">
                    <p className="text-xs text-slate-100">Bom dia! Tudo joia, e você?</p>
                    <p className="text-[9px] text-emerald-200/60 text-right mt-0.5 flex items-center justify-end gap-1">08:13 <CheckCheck className="w-3 h-3 text-cyan-300" /></p>
                  </div>
                  <div className="bg-[#1F2C34] rounded-lg rounded-tl-none px-3 py-2 max-w-[80%]">
                    <p className="text-xs text-slate-200">Partiu aquele café amanhã cedo?</p>
                    <p className="text-[9px] text-slate-500 text-right mt-0.5">08:14</p>
                  </div>
                  <div className="bg-[#005C4B] rounded-lg rounded-tr-none px-3 py-2 max-w-[80%] ml-auto flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-emerald-400/25 flex items-center justify-center shrink-0">
                      <Play className="w-3.5 h-3.5 text-emerald-300" />
                    </div>
                    <div className="flex-1">
                      <div className="h-1 rounded-full bg-emerald-900/60"><div className="h-1 w-2/3 rounded-full bg-emerald-400" /></div>
                      <p className="text-[9px] text-emerald-200/60 mt-1">0:32</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 px-1 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse-dot" />
                    <p className="text-[10px] text-slate-400 italic">gravando áudio...</p>
                  </div>
                  <div className="flex justify-center pt-1">
                    <span className="text-[10px] font-semibold px-3 py-1.5 rounded-full bg-slate-800/80 text-slate-400 border border-slate-700/60">
                      💤 Pausa de respiro — retorna em 14 min
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="comparacao" className="px-4 sm:px-6 pb-24" data-testid="landing-compare-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center mb-2">MaturaMax vs. outros maturadores</h2>
          <p className="text-sm sm:text-base text-slate-400 text-center mb-10">Veja na hora por que robôs comuns derrubam seus chips — e nós não.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-stretch">
            <div className="bg-[#161F32] border border-red-500/25 rounded-xl p-6 sm:p-7 animate-fade-up">
              <p className="font-heading font-bold text-red-400 mb-5 flex items-center gap-2">
                <X className="w-5 h-5" /> Maturadores comuns
              </p>
              <ul className="space-y-3.5">
                {[
                  "Usam scripts repetidos e mensagens engessadas",
                  "Não fazem pausas humanas nem modo dormir",
                  "Forçam o chip 24h seguidas (banimento rápido)",
                  "Se um chip cai, bane todos os outros do cliente (Efeito Dominó)",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-sm text-slate-400 leading-relaxed">
                    <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" /> {t}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative group animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="absolute -inset-0.5 rounded-2xl blur-lg bg-gradient-to-r from-emerald-500/40 to-cyan-500/40" />
              <div className="relative bg-[#161F32] border border-emerald-500/50 rounded-xl p-6 sm:p-7 h-full">
                <span className="absolute -top-3 right-6 text-[10px] font-extrabold px-3.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-emerald-950 uppercase tracking-widest shadow-[0_0_18px_rgba(16,185,129,0.55)]">
                  Recomendado para tráfego pago
                </span>
                <p className="font-heading font-bold text-emerald-400 mb-5 flex items-center gap-2">
                  <Flame className="w-5 h-5" /> MaturaMax
                </p>
                <ul className="space-y-3.5">
                  {[
                    "Esteira adaptativa de alta performance. O sistema inicia o aquecimento em uma rede colaborativa segura e eleva o volume de interações de forma gradual e automatizada. Isso prepara a saúde do seu chip para receber e enviar mais de 100 mensagens diárias com total blindagem anti-bloqueio.",
                    "Modo Dormir obrigatório na madrugada (08h às 22h30)",
                    "Simulador avançado de chamadas de voz e vídeo",
                    "Isolamento total de rede contra o efeito dominó",
                  ].map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-slate-200 leading-relaxed">
                      <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="planos" className="px-4 sm:px-6 pb-24" data-testid="landing-plans-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center mb-2">Planos para cada escala</h2>
          <p className="text-sm sm:text-base text-slate-400 text-center mb-10">Escolha seu plano ideal</p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            {PLAN_ORDER.map((key) => {
              const p = plans.find((x) => x.key === key);
              const popular = key === "m10";
              return (
                <div key={key} className="relative group w-full sm:w-[300px]">
                  <div className={`absolute -inset-0.5 rounded-2xl blur-lg transition-opacity duration-300 ${
                    popular
                      ? "bg-gradient-to-r from-emerald-500/40 to-cyan-500/40 opacity-100"
                      : "bg-gradient-to-r from-emerald-500/15 to-cyan-500/15 opacity-50 group-hover:opacity-90"
                  }`} />
                  <div
                    className={`relative bg-[#161F32] rounded-xl p-6 h-full transition-all duration-200 hover:-translate-y-1 ${popular ? "border border-emerald-500/50" : "border border-slate-800 hover:border-slate-700"}`}>
                  {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-extrabold px-3.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 text-emerald-950 uppercase tracking-widest shadow-[0_0_18px_rgba(16,185,129,0.55)] whitespace-nowrap">🔥 O Campeão de Vendas</span>}
                  <h3 className="font-heading text-lg font-bold text-slate-100 mt-2">{p?.label || `MaturaMax ${key.replace("m", "")}`}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-extrabold font-mono text-emerald-400">
                      {p ? `R$ ${p.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}` : "—"}
                    </span>
                    <span className="text-sm text-slate-500">/mês</span>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-400 mb-6">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> {p?.chip_limit === 1 ? "1 chip incluso" : `Até ${p?.chip_limit ?? "—"} chips simultâneos`}</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Aquecimento cruzado anti-ban</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400 shrink-0" /> Pix e Cartão em até 12x</li>
                  </ul>
                  <Link to="/cadastro">
                    <Button data-testid={`landing-plan-cta-${key}`}
                      className={`w-full font-semibold ${popular ? "bg-emerald-500 hover:bg-emerald-600 text-emerald-950" : "bg-slate-800 hover:bg-slate-700 text-slate-100 border border-slate-700"}`}>
                      Assinar {p?.label || "plano"}
                    </Button>
                  </Link>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex flex-col items-center gap-3" data-testid="landing-payment-seals">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <span className="text-[10px] font-extrabold italic px-2.5 py-1 rounded-md bg-[#1A1F71] text-white tracking-wide">VISA</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-slate-800 text-slate-200 border border-slate-700 flex items-center gap-1">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 -ml-2 inline-block" />
                Mastercard
              </span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#00A4E0] text-white">Elo</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#B3131B] text-white">Hipercard</span>
              <span className="text-[10px] font-bold px-2.5 py-1 rounded-md bg-[#32BCAD] text-white">Pix</span>
            </div>
            <p className="text-xs sm:text-sm text-slate-400 flex items-center justify-center gap-1.5 text-center">
              <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
              Pagamento 100% seguro via InfinitePay. Aceitamos Pix e cartão no checkout seguro.
            </p>
          </div>
        </div>
      </section>

      <section id="faq" className="px-4 sm:px-6 pb-24" data-testid="landing-faq-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-center mb-2">Perguntas Frequentes</h2>
          <p className="text-sm sm:text-base text-slate-400 text-center mb-10">Tudo o que você precisa saber antes de blindar seus chips.</p>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={item.q} value={`faq-${i}`}
                className="bg-[#161F32] border border-slate-800 rounded-xl px-5 transition-colors duration-150 data-[state=open]:border-emerald-500/40"
                data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-slate-100 hover:text-emerald-300 hover:no-underline py-4">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="px-4 sm:px-6 pb-20" data-testid="landing-final-cta">
        <div className="max-w-3xl mx-auto text-center relative">
          <div className="absolute -inset-6 bg-gradient-to-r from-emerald-500/20 via-cyan-500/10 to-emerald-500/20 rounded-3xl blur-2xl pointer-events-none" />
          <div className="relative bg-[#161F32] border border-emerald-500/30 rounded-2xl p-8 sm:p-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mb-3">Pronto para blindar seus chips?</h2>
            <p className="text-sm sm:text-base text-slate-400 mb-7">Crie sua conta em menos de 1 minuto e comece o aquecimento hoje mesmo.</p>
            <Link to="/cadastro">
              <Button data-testid="landing-final-cta-button"
                className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-bold h-12 px-8 text-base shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-200 hover:-translate-y-0.5">
                Começar agora <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-800/70 px-4 sm:px-6 pt-12 pb-8" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                <Flame className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="font-heading font-extrabold text-base">MaturaMax</span>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Aquecimento Inteligente de chips WhatsApp com inteligência de comportamento, voz ativa e automação completa na nuvem.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-300 mb-3">Produto</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="#recursos" className="hover:text-emerald-300 transition-colors">Recursos</a></li>
              <li><a href="#comparacao" className="hover:text-emerald-300 transition-colors">Comparação</a></li>
              <li><a href="#planos" className="hover:text-emerald-300 transition-colors">Planos</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-300 mb-3">Empresa</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="mailto:suporte@maturamax.com.br" className="hover:text-emerald-300 transition-colors">Contato</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-300 mb-3">Legal</p>
            <ul className="space-y-2 text-sm text-slate-400">
              <li><a href="#faq" className="hover:text-emerald-300 transition-colors">Perguntas frequentes</a></li>
              <li><Link to="/termos" className="hover:text-emerald-300 transition-colors">Termos de Uso</Link></li>
              <li><Link to="/privacidade" className="hover:text-emerald-300 transition-colors">Privacidade</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto border-t border-slate-800/70 pt-6 text-center">
          <p className="text-xs text-slate-500">© 2026 MaturaMax. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
